'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@/primitives';

type InbreedingCoefficientInfoModalProps = {
  open: boolean;
  onClose: () => void;
};

const TYPICAL_VALUES = [
  {
    relationship: 'Animal mated to its own parent (eg sire/daughter)',
    coefficient: '25%',
  },
  {
    relationship: 'Half-sib matings (parents have a common sire or dam)',
    coefficient: '12.5%',
  },
  {
    relationship: 'Full-sib matings (parents have a common sire and dam)',
    coefficient: '25%',
  },
  {
    relationship: 'Animal has a single common great-grandparent',
    coefficient: '3.1%',
  },
];

const LETHAL_RECESSIVE_OUTCOMES = [
  {
    mating: 'Carrier (Aa) × Non-carrier (AA)',
    normal: '50%',
    carrier: '50%',
    affected: '0%',
  },
  {
    mating: 'Carrier (Aa) × Carrier (Aa)',
    normal: '25%',
    carrier: '50%',
    affected: '25%',
  },
];

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="titlemedium"
      component="h3"
      gutterBottom
      sx={{
        fontWeight: 700,
      }}
    >
      {children}
    </Typography>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="titlesmall"
      component="h4"
      sx={{
        fontWeight: 600,
        mt: 2,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

export default function InbreedingCoefficientInfoModal({
  open,
  onClose,
}: InbreedingCoefficientInfoModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{ paper: { sx: { mb: { xs: 2 } } } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
        }}
      >
        About the Inbreeding Coefficient
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* What it is */}
          <Box>
            <Typography variant="bodymedium" gutterBottom>
              In general terms, inbreeding is where the sire and dam have
              ancestors in common. The level of inbreeding is simply how closely
              related these relatives are across the sire and dam lines.
            </Typography>
            <Typography variant="bodymedium" gutterBottom>
              The standard mathematical measure for the level of inbreeding is
              the <strong>Inbreeding Coefficient</strong>. It indicates the
              probability (between 0% and 100%) that genes at a randomly chosen
              location in the DNA are identical by descent. The technique
              assumes that there are 2 forms of a gene and that each form has an
              equal chance to be passed on to the next generation.
            </Typography>
            <Typography variant="bodymedium">
              One limitation on calculating inbreeding coefficients is the depth
              of pedigrees available. Animals with a shallow pedigree may have a
              low inbreeding coefficient simply because their related ancestors
              are not on the database.
            </Typography>
          </Box>

          <Divider />

          {/* Understanding the numbers */}
          <Box>
            <SectionHeading>
              Understanding the Inbreeding Numbers
            </SectionHeading>
            <Typography variant="bodymedium" gutterBottom>
              <strong>Inbreeding Coefficient</strong> — expressed as a
              percentage value. A low inbreeding coefficient means a low level
              of inbreeding. In most beef cattle breed societies, the vast
              majority of animals have an inbreeding coefficient of less than
              10%; inbreeding coefficients over 30% are unusual, and over 40%
              are rare.
            </Typography>
            <Typography variant="bodymedium">
              <strong>Generations</strong> — adjacent to the inbreeding
              coefficient, there are two numbers indicating the minimum number
              of generations in the pedigree and, in brackets, the average
              number of generations in the pedigree as currently available on
              the database. Deeper pedigrees will result in a more accurate
              calculation of the inbreeding coefficient, particularly where the
              inbreeding coefficient is small.
            </Typography>
          </Box>

          <Divider />

          {/* Typical values */}
          <Box>
            <SectionHeading>Typical Inbreeding Coefficients</SectionHeading>
            <Typography variant="bodymedium" sx={{ mb: 1.5 }}>
              Typical inbreeding coefficients for various relationships are
              given in Table 1. The inbreeding coefficient will be higher if the
              ancestors in the pedigree are also inbred themselves.
            </Typography>
            <Typography
              variant="bodysmall"
              sx={{
                display: 'block',
                mb: 1,
                fontStyle: 'italic',
                color: 'text.secondary',
              }}
            >
              Table 1. Inbreeding coefficients for various inbred relationships
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Relationship</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Inbreeding Coefficient (%) *</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {TYPICAL_VALUES.map((row) => (
                    <TableRow key={row.relationship}>
                      <TableCell>{row.relationship}</TableCell>
                      <TableCell align="right">{row.coefficient}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="bodysmall"
              sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}
            >
              * Minimum value — will be higher if ancestors are themselves
              inbred.
            </Typography>
            <Typography variant="bodymedium" sx={{ mt: 2 }}>
              Not surprisingly, smaller populations tend to have proportionally
              more animals with higher inbreeding coefficients than larger
              populations — simply because there are fewer candidate animals to
              select from.
            </Typography>
          </Box>

          <Divider />

          {/* How Inbreeding Works */}
          <Box>
            <SectionHeading>How Inbreeding Works</SectionHeading>

            <SubHeading>Genetic diversity and selection</SubHeading>
            <Typography variant="bodymedium">
              Selection in animal breeding systems uses genetic diversity to
              improve the population by selecting superior animals for desirable
              traits. The more genetic variation in a population, the greater
              the potential improvement in each generation. Inbreeding reduces
              that genetic diversity — and therefore the scope for future
              genetic gain.
            </Typography>

            <SubHeading>Genes, alleles and homozygosity</SubHeading>
            <Typography variant="bodymedium" gutterBottom>
              Genetic information is stored in chromosomes, which are made up of
              DNA. Genes are sections of DNA that occur in pairs, at a specific
              location (locus; plural: loci) on a chromosome. The different
              forms of a gene — usually 2 — are called <strong>alleles</strong>.
              Where both alleles at a locus are the same, the animal is{' '}
              <strong>homozygous</strong> at that locus; where they differ, it
              is <strong>heterozygous</strong>.
            </Typography>
            <Typography variant="bodymedium">
              In general the two alleles have an equal influence on performance,
              so the heterozygous form sits midway between the two homozygous
              forms. In some cases one allele has the dominant effect and the
              other only shows when homozygous — these are called{' '}
              <strong>dominant</strong> and <strong>recessive</strong> genes.
            </Typography>

            <SubHeading>How the coefficient is calculated</SubHeading>
            <Typography variant="bodymedium" gutterBottom>
              The coefficient of inbreeding (as proposed by Sewell Wright in
              1922) is the probability that two alleles at a randomly chosen
              locus are identical by descent — that is, inherited from the same
              common ancestor. As inbreeding increases, so does the frequency of
              homozygous alleles, reducing variation across the population.
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
            >
              <Typography
                variant="bodysmall"
                sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}
              >
                Worked example — single common great-grandparent (3.1%)
              </Typography>
              <Typography variant="bodymedium">
                The probability that the <strong>sire</strong> inherited a
                particular allele from that great-grandparent is ½ × ½ ={' '}
                <strong>¼</strong> (two generations). The same probability
                applies through the <strong>dam&apos;s</strong> line. Because
                that allele could have been passed down via either of two paths,
                the overall probability that both parents carry the same allele
                by descent is ¼ × ¼ × 2 = <strong>3.125%</strong> — matching the
                value in Table 1.
              </Typography>
            </Paper>

            <SubHeading>Line breeding and out-crossing</SubHeading>
            <Typography variant="bodymedium" gutterBottom>
              <strong>Line breeding</strong> is a form of controlled inbreeding
              that attempts to fix desirable homozygous alleles in a population
              through planned selection. However, it can also fix undesirable
              alleles, potentially leading to inbreeding depression or lethal
              recessives (see below).
            </Typography>
            <Typography variant="bodymedium">
              The opposite of inbreeding is <strong>out-crossing</strong>, which
              is a contributor to hybrid vigour. Notably, two inbred animals
              that are unrelated to each other will produce fully out-crossed
              progeny with an inbreeding coefficient of zero — it is possible to
              go from inbred to out-crossed in a single generation.
            </Typography>
          </Box>

          <Divider />

          {/* Inbreeding Depression */}
          <Box>
            <SectionHeading>Inbreeding Depression</SectionHeading>
            <Typography variant="bodymedium" gutterBottom>
              Inbreeding depression describes the reduction in performance and
              viability that can result from increased inbreeding levels
              (reduced genetic variation). Reproductive fitness tends to be
              affected more than production traits in this respect.
            </Typography>
            <Typography variant="bodymedium" gutterBottom>
              The extent of inbreeding depression depends on the population
              being measured, the level of genes already fixed in the
              population, the frequency of any deleterious genes present, and
              chance effects of which particular genes are affected.
            </Typography>
            <Typography variant="bodymedium">
              There is no defined acceptable limit for inbreeding in domestic
              animal populations. However, inbreeding depression is likely to be
              more apparent once inbreeding levels exceed 10%. As a very rough
              guide, there is often a 2–20% decrease in performance per 10% of
              inbreeding coefficient.
            </Typography>
          </Box>

          <Divider />

          {/* Undesirable and Lethal Recessives */}
          <Box>
            <SectionHeading>Undesirable and Lethal Recessives</SectionHeading>
            <Typography variant="bodymedium" gutterBottom>
              Some undesirable recessive genes are only expressed in their
              homozygous form. In these cases, the dominant desirable allele is
              expressed in animals that carry one copy (heterozygous), meaning
              carriers appear completely normal.
            </Typography>
            <Typography variant="bodymedium" gutterBottom>
              The <strong>Arthrogryposis Multiplex (AM)</strong> gene is an
              example of a lethal recessive. The undesirable allele (a) in its
              homozygous form (aa) causes affected calves to have a bent and
              twisted spine and limited muscle development; affected calves are
              generally stillborn. Conversely, animals with at least one copy of
              the dominant allele (A) — either homozygous (AA) or heterozygous
              (Aa) — are normal, unaffected calves. The heterozygous animals
              (Aa) are called <strong>carriers</strong> because they can pass
              the undesirable allele to the next generation undetected.
            </Typography>
            <Typography variant="bodymedium" sx={{ mb: 1.5 }}>
              Table 2 shows the probability of offspring outcomes depending on
              the carrier status of the parents.
            </Typography>
            <Typography
              variant="bodysmall"
              sx={{
                display: 'block',
                mb: 1,
                fontStyle: 'italic',
                color: 'text.secondary',
              }}
            >
              Table 2. Probability of mating outcomes with a carrier parent
              (using AM as an example)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Mating</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Normal (AA)</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Carrier (Aa)</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Affected (aa)</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {LETHAL_RECESSIVE_OUTCOMES.map((row) => (
                    <TableRow key={row.mating}>
                      <TableCell>{row.mating}</TableCell>
                      <TableCell align="center">{row.normal}</TableCell>
                      <TableCell align="center">{row.carrier}</TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color:
                            row.affected !== '0%' ? 'error.main' : 'inherit',
                        }}
                      >
                        {row.affected}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="bodymedium" sx={{ mt: 2 }}>
              A genetic test is now available to determine which alleles an
              animal actually carries — that is, whether it is homozygous for
              the desirable allele (AA) or is a carrier (Aa). This information
              is used in some breed society programs to identify and manage the
              spread of such recessive conditions.
            </Typography>
          </Box>

          <Divider />

          {/* How BreedLOGIC calculates */}
          <Box>
            <SectionHeading>
              How BreedLOGIC Calculates Inbreeding
            </SectionHeading>
            <Typography variant="bodymedium" gutterBottom>
              BreedLOGIC uses the same mathematical method as other industry
              systems, applied to the pedigree data held in the BreedLOGIC
              database.
            </Typography>
            <Typography variant="bodymedium" gutterBottom>
              BreedLOGIC has access to a deeper and more complete ancestry
              record than some other systems. As a result, inbreeding
              coefficients and pedigree depth statistics may differ slightly
              from figures shown in Internet Solutions or ILROnline, and are
              generally more accurate. In particular:
            </Typography>
            <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
              <Typography component="li" variant="bodymedium">
                Small non-zero coefficients are displayed as their actual
                calculated value rather than being rounded to 0%.
              </Typography>
              <Typography component="li" variant="bodymedium" sx={{ mt: 0.5 }}>
                Generation depth statistics may be slightly higher than other
                systems because BreedLOGIC has broader ancestry data on record —
                this reflects improved accuracy rather than a calculation
                difference.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
